import React from 'react';
import { useTranslation } from 'react-i18next';
import ChartComponent from '../ChartComponent';
import { ComparisonDetailMembersDataType } from '../../types';
import Section from '../common/Section/Section';
import useDimensions from '../../hooks/useDimensions';

type Props = {
    comparison: ComparisonDetailMembersDataType['comparison'];
};

const MatchChartSection = ({ comparison }: Props) => {
    const { width } = useDimensions();
    const { t } = useTranslation(['analyses', 'translation']);

    if (!comparison) {
        return null;
    }

    let chartData: any = {};
    let labels: string[] = [];
    let chartOptions: any = {};

    const ratios = Object.entries(comparison.data).map(([key, value]) => {
        const neshodaLength = value.neshoda?.length || 0;
        const shodaLength = value.shoda?.length || 0;
        const totalLength = neshodaLength + shodaLength;
        const ratio = totalLength > 0 ? ((shodaLength / totalLength) * 100).toFixed(1) : 0;
        return { key, ratio: ratio };
    });

    const style = getComputedStyle(document.body);
    const colors = [style.getPropertyValue('--clr-1'), style.getPropertyValue('--clr-7')];
    labels = ratios.map((ratio) => ratio.key);
    chartData = {
        labels,
        datasets: [
            {
                label: t('avg-agreement') + ' (%)',
                type: 'line',
                data: Array(ratios.length).fill(comparison.match_stat),
                backgroundColor: colors[1],
                borderColor: colors[1],
                pointStyle: 'dash',
                borderDash: [10, 5]
            },
            {
                label: t('meetings-agreement') + ' (%)',
                data: ratios.map((ratio) => ratio.ratio),
                backgroundColor: colors[0]
            }
        ]
    };

    chartOptions = {
        type: 'bar',
        indexAxis: 'x',
        maintainAspectRatio: false,
        aspectRatio: 0.7,
        responsive: true,
        scales: {
            y: {
                ticks: {
                    beginAtZero: true,
                    callback: (value: string, index: string | number, values: any) => {
                        if (chartOptions.indexAxis === 'x') {
                            return value + ' %';
                        }
                        return comparison.zasedani[index as any].name;
                    }
                },
                max: 100
            },
            x: {
                ticks: {
                    beginAtZero: true,
                    callback: (value: string, index: string | number, values: any) => {
                        if (chartOptions.indexAxis === 'y') {
                            return value + ' %';
                        }
                        return comparison.zasedani[index as any].name;
                    }
                },
                max: chartOptions.indexAxis === 'y' ? 100 : undefined
            }
        }
    };

    switch (true) {
        case ratios.length > 0 && ratios.length <= 30:
            chartOptions.indexAxis = 'x';
            chartOptions.aspectRatio = 0.7;
            break;
        case ratios.length > 30 && width <= 576:
            chartOptions.indexAxis = 'y';
            chartOptions.aspectRatio = 0.5;
            break;
        default:
            break;
    }

    return (
        <Section title={t('agreement-on-meetings')}>
            <ChartComponent
                type='bar'
                data={chartData}
                options={chartOptions}
                height={undefined}
                width={undefined}
                xTitle={undefined}
                yTitle={undefined}
                aria={undefined}
            />
        </Section>
    );
};

export default MatchChartSection;
